import React, { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  useCreateCatalogMethod,
  useUpdateCatalogMethod,
} from "../../../stores/MasterTables/therapies.mutations";
import ColorField, { isValidHexColor } from "./ColorField";

/**
 * Create/edit a tarif-agnostic method.
 *
 * The billable rows underneath are NOT edited here — they live on the Heilmittel
 * tab, because each one carries payer-specific data (Positionsnummer, price,
 * duration). This dialog only owns what is true of the therapy regardless of who
 * pays.
 */

interface Props {
  open: boolean;
  method: any | null;
  /** All methods, used only to offer the colours already in use as a palette. */
  methods?: any[];
  onClose: () => void;
}

const ModalMethod: React.FC<Props> = ({ open, method, methods = [], onClose }) => {
  const isEdit = !!method;
  const createMethod = useCreateCatalogMethod();
  const updateMethod = useUpdateCatalogMethod();

  const [label, setLabel] = useState<string>(method?.label ?? "");
  const [description, setDescription] = useState<string>(method?.description ?? "");
  const [color, setColor] = useState<string | null>(method?.color ?? null);
  const [isPassive, setIsPassive] = useState<boolean>(method?.isPassive ?? false);

  const busy = createMethod.isPending || updateMethod.isPending;

  const colorInvalid = !!color && !isValidHexColor(color);

  const handleSave = async () => {
    const payload = {
      label: label.trim(),
      description: description.trim() || null,
      // Always sent, so clearing the swatch actually clears the stored colour —
      // the server only leaves it alone when the field is absent.
      color,
      isPassive,
    };
    try {
      if (isEdit) {
        await updateMethod.mutateAsync({ id: method.id, data: payload as any });
      } else {
        await createMethod.mutateAsync(payload as any);
      }
      onClose();
    } catch {
      // notification surfaced by the mutation
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? `Methode bearbeiten — ${method.id}` : "Neue Methode anlegen"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Bezeichnung"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            required
            helperText={
              isEdit
                ? "Die ID bleibt unverändert."
                : "Die ID wird aus der Bezeichnung abgeleitet."
            }
          />
          <TextField
            label="Beschreibung"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <ColorField
            value={color}
            onChange={setColor}
            suggestions={(methods ?? []).map((m: any) => m.color).filter(Boolean)}
            helperText="Farbe im Kalender und auf Planungs-Chips. Leer = Standardfarbe des Clients."
          />

          <FormControlLabel
            control={
              <Checkbox checked={isPassive} onChange={(e) => setIsPassive(e.target.checked)} />
            }
            label="Passive Maßnahme (z. B. Wärme, Packung)"
          />

          {isEdit && (
            <Alert severity="info">
              <Typography variant="body2" gutterBottom>
                Abrechnungszeilen werden im Tab <strong>Heilmittel</strong> gepflegt — dort hängen
                Positionsnummer, Preis und Dauer je Tarifgruppe.
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {(method.lineItems ?? []).length === 0 ? (
                  <Typography variant="caption">Aktuell keine Abrechnungszeile.</Typography>
                ) : (
                  (method.lineItems ?? []).map((i: any) => (
                    <Chip key={i.id} label={i.id} size="small" variant="outlined" />
                  ))
                )}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={busy || !label.trim() || colorInvalid}
        >
          {busy ? "Speichert…" : "Speichern"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalMethod;
